import HorizontalDatePicker from '@/components/HorizontalDatePicker';
import { type StatusChip } from '@/components/StatusFilterChips';
import WorkspaceCanvas from '@/components/WorkspaceCanvas';
import { usePendingReservations } from '@/features/reservations/hooks/useReservations';
import { featureFlags } from '@/env';
import { type DisplayStatus, getStatusColor } from '@/utils/status';
import ReservationInspector from '@/features/reservations/components/ReservationInspector';
import ReservationPauseCard from '@/features/reservations/components/ReservationPauseCard';
import ListView from '@/features/reservations/views/ListView';
import TimelineView from '@/features/reservations/views/TimelineView';
import WeekView from '@/features/reservations/views/WeekView';
import { todayString } from '@/utils/datetime';
import { useT } from '@/i18n';
import { sectionRouter } from '@/utils/navigateMain';
import { useBottomBarInset } from '@/utils/useBottomBarInset';
import { useIsWideViewport, useSupportsDockedDetail } from '@/utils/useMediaQuery';
import type { Reservation } from '@restaurant/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { Link, NavLeft, NavRight, NavTitle, Navbar, Page, Subnavbar, f7 } from 'framework7-react';
import type { CSSProperties } from 'react';
import { useEffect, useState } from 'react';

type ViewMode = 'list' | 'timeline' | 'week';

// Each view carries its own iOS-system accent, mirroring the per-destination
// colours in the desktop sidebar: resting icons read as a colored index and
// the selected segment lifts as a solid thumb in that colour.
const VIEW_TABS: { id: ViewMode; icon: string; color: string }[] = [
  { id: 'list', icon: 'list_bullet', color: '#0a84ff' },
  { id: 'timeline', icon: 'chart_bar', color: '#ff9500' },
  { id: 'week', icon: 'calendar', color: '#5e5ce6' },
];

/**
 * Reservations workspace: a top-level page that hosts multiple representations
 * (list, timeline, week) of the same underlying reservation
 * data. The active view fills the main canvas; selecting a reservation opens
 * an inspector - docked on wide layouts, modal sheet on phones.
 *
 * Owns: `date`, `view`, `selectedReservationId`. Views receive these as props
 * and ask the workspace to mutate selection through `onSelectReservation`.
 */
interface RouteProps {
  f7route?: { params?: { id?: string } };
}

export default function ReservationsWorkspacePage({ f7route: _f7route }: RouteProps) {
  const t = useT();
  // `isWide` drives the desktop navbar chrome (the segmented view switcher,
  // the "New reservation" text link). `canDockDetail` is a higher breakpoint
  // that additionally decides whether a selected reservation docks as an
  // inspector or pushes as a full page - see useSupportsDockedDetail.
  const isWide = useIsWideViewport();
  const canDockDetail = useSupportsDockedDetail();
  const [date, setDate] = useState<string>(todayString());
  const [view, setView] = useState<ViewMode>('list');
  // Status filter for the list view (client-side over the day's reservations).
  const [statusFilter, setStatusFilter] = useState<DisplayStatus | 'all'>('all');
  // Selection is only used by the wide-layout docked inspector. On phones we
  // push a separate detail page instead of tracking selection in the workspace.
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const { data: pending } = usePendingReservations();
  const pendingCount = Array.isArray(pending) ? pending.length : 0;

  // Pull-to-refresh - only for the list view; timeline/week own their scroll
  // (and auto-scroll to "now"), so a pull gesture there would fight their inner
  // scroller. Refetches the reservations queries (day list + pending) AND the
  // two that back the availability strip - config (enabled/pausedUntil) and
  // opening hours - so a pull refreshes the whole page, banner included.
  const queryClient = useQueryClient();
  const onListRefresh =
    view === 'list'
      ? () =>
          Promise.all([
            queryClient.refetchQueries({ queryKey: ['reservations'] }),
            queryClient.refetchQueries({ queryKey: ['reservation-config'] }),
            queryClient.refetchQueries({ queryKey: ['opening-hours'] }),
          ])
      : undefined;

  // If we narrow below the docking breakpoint (rotate / resize), drop the
  // selection - the user would otherwise have a lingering selected card with no
  // panel visible.
  useEffect(() => {
    if (!canDockDetail && selectedId) setSelectedId(null);
  }, [canDockDetail, selectedId]);

  const handleSelect = (reservation: Reservation) => {
    if (canDockDetail) {
      setSelectedId(reservation.id);
    } else {
      // iOS-native push, with back-swipe and navbar transition. `sectionRouter()`
      // rather than a hardcoded `views.main`: it resolves the view that hosts this
      // section per layout, and returns null (instead of throwing on `.router`)
      // when that view isn't mounted yet or has been torn down.
      sectionRouter()?.navigate(`/reservations/${reservation.id}`);
    }
  };

  // Status chips for the list view - one per display-status section (+ All),
  // coloured to match the section badges. Filter is client-side within the day.
  const statusChips: StatusChip<DisplayStatus | 'all'>[] = [
    { key: 'all', label: t.reservations.all },
    ...(
      [
        'pending',
        'waiting',
        'arrived',
        // Seated / In progress are feature-flagged off by default (see env.featureFlags).
        ...(featureFlags.seatedAndInProgress ? (['seated', 'in-progress'] as const) : []),
        'completed',
        'no-show',
        // Rescheduled is feature-flagged off by default (see env.featureFlags).
        ...(featureFlags.reschedule ? (['rescheduled'] as const) : []),
        'cancelled',
        'rejected',
      ] as DisplayStatus[]
    ).map((ds): StatusChip<DisplayStatus | 'all'> => ({
      key: ds,
      label: t.reservations.displayStatus[ds],
      color: getStatusColor(ds),
    })),
  ];

  const renderView = () => {
    const props = { date, selectedId, onSelectReservation: handleSelect };
    switch (view) {
      case 'list':
        return (
          <>
            {/* Reservation-availability strip (Open / Closed / Paused), pinned
                above the day's list - the analog of OrderPauseCard on Orders. */}
            <ReservationPauseCard />
            {/* Both viewports: the filter is a navbar button (next to the bell);
                the active filter shows as a content banner. */}
            {statusFilter !== 'all' &&
              (() => {
                const chip = statusChips.find((c) => c.key === statusFilter);
                return (
                  <div className="wsp-filter-banner">
                    {chip?.color && (
                      <span className="wsp-filter-dot" style={{ background: chip.color }} />
                    )}
                    <span className="wsp-filter-banner-label">{chip?.label}</span>
                  </div>
                );
              })()}
            <ListView {...props} statusFilter={statusFilter} />
          </>
        );
      case 'timeline':
        return <TimelineView {...props} />;
      case 'week':
        return <WeekView {...props} />;
    }
  };

  // Each view picks its own scoping control for the navbar subrow. Every current
  // view is scoped by a single day, so they all share the date picker. New views
  // (Week, Month, range Timeline …) just add a case here without touching the
  // rest of the shell.
  const renderContextControl = () => {
    switch (view) {
      case 'list':
      case 'timeline':
      case 'week':
        return <HorizontalDatePicker selectedDate={date} onDateSelect={setDate} />;
    }
  };
  const contextControl = renderContextControl();

  // Full-height shell (`pageContent={false}`): scrollers/stickies/floating
  // controls slide under the blurred bar via `--rsv-nav-offset`. That offset is
  // derived purely in CSS (styles.css: `[data-name='reservations']:has(.subnavbar)`)
  // - navbar height, plus the date-strip subnavbar when a view has one. CSS
  // instead of a JS measurement so it can't drift to a stale,
  // too-small value when the page is navigated away and back.
  // Measured tab-bar height → `--rsv-bottom-offset`, so list/calendar/timeline
  // scroll content clears (and slides under) the bottom tab bar instead of
  // being clipped at it.
  const bottomBarInset = useBottomBarInset();

  // Wide: a segmented pill in the navbar title. Mobile can't fit four tabs
  // there, so it gets a compact "current view ▾" dropdown trigger in the
  // leading slot instead (see openViewPicker below).
  const viewSwitcher = (
    <div className="rsv-view-switcher" role="tablist">
      {VIEW_TABS.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={view === tab.id}
          className={`rsv-view-tab${view === tab.id ? ' rsv-view-tab-active' : ''}`}
          style={{ '--rsv-view-color': tab.color } as CSSProperties}
          onClick={() => setView(tab.id)}
        >
          <i className="f7-icons">{tab.icon}</i>
          <span className="rsv-view-tab-label">{t.views[tab.id]}</span>
        </button>
      ))}
    </div>
  );

  const openViewPicker = (targetEl: HTMLElement) => {
    f7.actions
      .create({
        // Anchor to the trigger so on regular-width (iPad/desktop) F7 converts
        // the sheet to a popover that closes on outside click; without a target
        // it stays a bottom sheet even on desktop and the backdrop dismiss is
        // unreliable.
        targetEl,
        // Single group, no Cancel button - tapping a view (or the backdrop)
        // closes the sheet, so a separate Cancel row and its gap are noise.
        buttons: [
          VIEW_TABS.map((tab) => ({
            text: t.views[tab.id],
            bold: tab.id === view,
            onClick: () => setView(tab.id),
          })),
        ],
      })
      .open();
  };

  // The title itself is the picker - same affordance as iOS Calendar
  // ("Day ▾" / "Week ▾") or Mail ("Inbox ▾"). One tap target, no extra icon.
  const mobileViewTrigger = (
    <button
      type="button"
      className="rsv-view-title-trigger"
      onClick={(e) => openViewPicker(e.currentTarget)}
    >
      <span>{t.views[view]}</span>
      <i className="f7-icons">chevron_down</i>
    </button>
  );

  // Mobile status filter: an icon button next to the bell (list view only) that
  // opens an action-sheet picker. The active filter is surfaced by a content
  // banner, not on the button.
  const openStatusPicker = (targetEl: HTMLElement) => {
    f7.actions
      .create({
        // Anchor to the trigger so on regular-width (iPad/desktop) F7 converts
        // the sheet to a popover that closes on outside click; without a target
        // it stays a bottom sheet even on desktop and the backdrop dismiss is
        // unreliable.
        targetEl,
        buttons: [
          statusChips.map((c) => ({
            text: c.label,
            bold: c.key === statusFilter,
            onClick: () => setStatusFilter(c.key),
          })),
        ],
      })
      .open();
  };
  const mobileFilterTrigger = (
    <button
      type="button"
      className={`wsp-filter-trigger${statusFilter !== 'all' ? ' is-active' : ''}`}
      onClick={(e) => openStatusPicker(e.currentTarget)}
      aria-label={t.reservations.filterAria}
    >
      <i className="f7-icons">line_horizontal_3_decrease</i>
    </button>
  );

  return (
    <Page name="reservations" pageContent={false}>
      <style>{`
        .page[data-name="reservations"] .navbar .badge { background-color: #ff3b30 !important; }
      `}</style>
      {/* On wide layouts the view switcher takes the navbar's title slot -
          Mac-style toolbar pattern. On phones the title stays "Reservations"
          and the switcher sits in its own row under the date strip, where
          there's enough horizontal room for the labels. */}
      <Navbar>
        <NavLeft>
          <Link
            href="/reservations/pending"
            iconF7="bell_fill"
            iconBadge={pendingCount > 0 ? pendingCount : undefined}
            badgeColor="red"
          />
          {view === 'list' && mobileFilterTrigger}
        </NavLeft>
        <NavTitle>{isWide ? viewSwitcher : mobileViewTrigger}</NavTitle>
        <NavRight>
          <Link
            href="/reservations/create"
            iconF7="plus"
            aria-label={t.reservations.newReservation}
          />
        </NavRight>
        {/* `no-touch-highlight` opts this scrollable date-strip subnavbar out of
            F7 v9's iOS 26 touch-scale (see `touch.touchHighlightElements` in App.tsx). */}
        {contextControl && <Subnavbar className="no-touch-highlight">{contextControl}</Subnavbar>}
      </Navbar>

      <WorkspaceCanvas
        isWide={canDockDetail}
        bottomBarInset={bottomBarInset}
        onRefresh={onListRefresh}
        main={renderView()}
        inspector={
          selectedId ? (
            <ReservationInspector reservationId={selectedId} onClose={() => setSelectedId(null)} />
          ) : null
        }
      />
    </Page>
  );
}
